import { useI18N } from '@susu/i18n/client'
import { Button, Icon, Loader } from '@susu/ui/components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Avo, { ErrorType } from '../../../Avo/Avo'
import { ScreenLayout } from '../../components/ScreenLayout'
import { SlotUnavailable } from '../../components/SlotUnavailable'
import { NO_PREFERENCE_IMAGE } from '../../config/constants'
import { flowProperties } from '../../lib/analytics'
import { useBookingStore } from '../../store'
import { getStoreDataByLocation } from '../StoreLocator/api/stores-api'
import { isSlotUnavailableError } from '../UserData/api/user-data-api'
import { getBooking, updateBooking } from './api/booking-api'
import {
  getEmployeeDataById,
  getEmployeeDataByWwResourceId,
  getNoPreferenceEmployee,
  resolveEmployeeImage,
} from './api/employee-api'
import type { DayAvailability } from './Timeslots.types'
import {
  fillDays,
  formatMonthDay,
  formatTime,
  formatWeekday,
  isDayAvailable,
  isSameMinute,
  toDatePart,
} from './Timeslots.utils'

interface TimeslotsProps {
  onClose?: () => void
}

// ---------------------------------------------------------------------------
// Date carousel
// - mobile (<768): hand-scroll, no arrows, ~3 tabs visible
// - md+: arrow carousel, 3 tabs
// ---------------------------------------------------------------------------
const MD_BREAKPOINT = 768
const DAY_GAP_PX = 8
const SLIDE_MS = 320

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  )
  useEffect(() => {
    const mql = window.matchMedia(query)
    const update = () => setMatches(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [query])
  return matches
}

function useVisibleCount() {
  return 3
}

interface DayPickerProps {
  days: DayAvailability[]
  selectedDate: string
  onSelect: (date: string) => void
}

interface DayCardProps {
  day: DayAvailability
  idx: number
  selectedDate: string
  onSelect: (date: string) => void
  width?: number
  className?: string
}

const DayCard = ({
  day,
  idx,
  selectedDate,
  onSelect,
  width,
  className = '',
}: DayCardProps) => {
  const isSelected = toDatePart(day.date) === toDatePart(selectedDate)
  const available = isDayAvailable(day)
  // Weekday and month names come from the locale, not from en.json.
  const language = useBookingStore((state) => state.language)

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      disabled={!available}
      onClick={() => available && onSelect(day.date)}
      className={[
        'flex flex-col items-center justify-center gap-[2px] shrink-0 p-20 rounded-lg border',
        'transition-[border-color] duration-150',
        isSelected
          ? 'border-border-selected'
          : available
            ? 'border-border'
            : 'border-border-disabled',
        !available ? 'cursor-default' : '',
        className,
      ].join(' ')}
      style={width !== undefined && width > 0 ? { width } : undefined}
      data-testid={`timeslots-day-${idx}`}
    >
      <span
        className={`text-sm leading-[20px] font-medium ${available ? 'text-text' : 'text-text-disabled'}`}
      >
        {formatMonthDay(day.date, language)}
      </span>
      <span
        className={`text-[12px] leading-[16px] font-light ${available ? 'text-text' : 'text-text-disabled'}`}
      >
        {formatWeekday(day.date, language)}
      </span>
    </button>
  )
}

const DayPicker = ({ days, selectedDate, onSelect }: DayPickerProps) => {
  const isMobile = useMediaQuery(`(max-width: ${MD_BREAKPOINT - 1}px)`)
  const visibleCount = useVisibleCount()
  const [startIndex, setStartIndex] = useState(0)
  const viewportRef = useRef<HTMLDivElement>(null)
  const mobileScrollRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState(0)

  useEffect(() => {
    if (isMobile) return
    const el = viewportRef.current
    if (!el) return
    const update = () => setViewportWidth(el.clientWidth)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [isMobile])

  // Mobile: keep the selected date in view via native scroll
  useEffect(() => {
    if (!isMobile) return
    const idx = days.findIndex(
      (d) => toDatePart(d.date) === toDatePart(selectedDate),
    )
    if (idx === -1) return
    const el = mobileScrollRef.current?.querySelector<HTMLElement>(
      `[data-testid="timeslots-day-${idx}"]`,
    )
    el?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })
  }, [isMobile, selectedDate, days])

  // Desktop: clamp startIndex when the visible count shrinks
  useEffect(() => {
    if (isMobile) return
    setStartIndex((i) => Math.min(i, Math.max(0, days.length - visibleCount)))
  }, [isMobile, visibleCount, days.length])

  // Desktop: keep the selected date inside the visible window
  useEffect(() => {
    if (isMobile) return
    const idx = days.findIndex(
      (d) => toDatePart(d.date) === toDatePart(selectedDate),
    )
    if (idx === -1) return
    setStartIndex((current) => {
      if (idx < current) return idx
      if (idx >= current + visibleCount)
        return Math.max(0, idx - visibleCount + 1)
      return current
    })
    // `startIndex` is omitted intentionally — the functional updater reads the
    // latest value without needing it as a reactive dependency.
  }, [isMobile, selectedDate, days, visibleCount])

  const canGoPrev = startIndex > 0
  const canGoNext = startIndex + visibleCount < days.length

  const prev = useCallback(() => setStartIndex((i) => Math.max(0, i - 1)), [])
  const next = useCallback(
    () => setStartIndex((i) => Math.min(days.length - visibleCount, i + 1)),
    [days.length, visibleCount],
  )

  // ── Mobile: hand-scroll, no arrows ──────────────────────────────────────
  if (isMobile) {
    return (
      <div
        ref={mobileScrollRef}
        className="flex gap-8 overflow-x-auto px-16 [scroll-snap-type:x_mandatory] [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="radiogroup"
      >
        {days.map((day, idx) => (
          <DayCard
            key={day.date}
            day={day}
            idx={idx}
            selectedDate={selectedDate}
            onSelect={onSelect}
            className="w-[calc((100%-16px)/3)] [scroll-snap-align:center]"
          />
        ))}
      </div>
    )
  }

  // ── Desktop / tablet: arrow carousel ────────────────────────────────────
  const itemWidth =
    viewportWidth > 0
      ? (viewportWidth - (visibleCount - 1) * DAY_GAP_PX) / visibleCount
      : 0
  const translateX = startIndex * (itemWidth + DAY_GAP_PX)

  return (
    <div
      className="flex items-center justify-center gap-24 px-16"
      role="radiogroup"
    >
      <button
        type="button"
        onClick={prev}
        disabled={!canGoPrev}
        aria-label="Previous dates"
        className={[
          'shrink-0 flex items-center justify-center w-32 h-32 rounded-full border',
          'transition-[border-color,opacity] duration-150',
          canGoPrev
            ? 'border-border bg-background text-icon hover:border-border-selected'
            : 'border-border-disabled bg-background text-text-disabled cursor-default opacity-40',
        ].join(' ')}
        data-testid="timeslots-day-prev"
      >
        <Icon name="chevron-left" size={16} />
      </button>

      <div ref={viewportRef} className="flex-1 overflow-hidden">
        <div
          className="flex will-change-transform"
          style={{
            gap: DAY_GAP_PX,
            transform: `translateX(-${translateX}px)`,
            transition: `transform ${SLIDE_MS}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
          }}
        >
          {days.map((day, idx) => (
            <DayCard
              key={day.date}
              day={day}
              idx={idx}
              selectedDate={selectedDate}
              onSelect={onSelect}
              width={itemWidth}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={next}
        disabled={!canGoNext}
        aria-label="Next dates"
        className={[
          'shrink-0 flex items-center justify-center w-32 h-32 rounded-full border',
          'transition-[border-color,opacity] duration-150',
          canGoNext
            ? 'border-border bg-background text-icon hover:border-border-selected'
            : 'border-border-disabled bg-background text-text-disabled cursor-default opacity-40',
        ].join(' ')}
        data-testid="timeslots-day-next"
      >
        <Icon name="chevron-right" size={16} />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Time slots grid — 3 columns matching Figma
// ---------------------------------------------------------------------------
interface TimeSlotsGridProps {
  day: DayAvailability
  selectedSlot: string | null
  rescheduleSlot: string | null
  onSelect: (start: string) => void
}

const TimeSlotsGrid = ({
  day,
  selectedSlot,
  rescheduleSlot,
  onSelect,
}: TimeSlotsGridProps) => (
  <div
    className="grid grid-cols-3 gap-8 px-16 justify-items-center"
    data-testid="timeslots-grid"
  >
    {day.timeSlots.map(({ start, available }) => {
      const isRescheduleOrigin =
        Boolean(rescheduleSlot) && isSameMinute(start, rescheduleSlot!)
      const isSelected =
        Boolean(selectedSlot) && isSameMinute(start, selectedSlot!)
      const isDisabled = !available || isRescheduleOrigin

      return (
        <button
          key={start}
          type="button"
          disabled={isDisabled}
          onClick={() => !isDisabled && onSelect(start)}
          className={[
            'h-44 flex items-center justify-center rounded-full px-20 border',
            'text-[16px] leading-[24px] font-light transition-[background-color,border-color,color] duration-150',
            isRescheduleOrigin
              ? 'bg-surface-secondary border-text-secondary text-text-secondary cursor-default'
              : isSelected
                ? 'bg-button border-button text-text-button'
                : available
                  ? 'border-transparent text-text hover:bg-surface-alt'
                  : 'border-transparent text-text-disabled cursor-default',
          ].join(' ')}
          data-testid={`timeslots-slot-${formatTime(start).replace(':', '-')}-${isRescheduleOrigin ? 'current' : available ? 'available' : 'unavailable'}`}
        >
          {formatTime(start)}
        </button>
      )
    })}
  </div>
)

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------
export const Timeslots = ({ onClose }: TimeslotsProps) => {
  const { t } = useI18N()
  const store = useBookingStore()
  const {
    employeeId,
    bookingId,
    locationCode,
    wwResourceId,
    navigate,
    setState,
    selectedEmployee,
    advisorStepSkipped,
  } = store

  const queryClient = useQueryClient()
  const isReschedule = Boolean(bookingId)

  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [rescheduleSlot, setRescheduleSlot] = useState<string | null>(null)

  const updateMutation = useMutation({
    mutationFn: updateBooking,
    onSuccess: () => {
      setState({
        screen: 'confirmation',
        screenHistory: [],
        confirmationVariant: 'rescheduled',
      })
    },
  })

  const isSlotUnavailable = isSlotUnavailableError(updateMutation.error)

  // The slot filled up between selection and submit: drop it and refetch
  // availability so it is not offered again. Stays on this screen, unlike
  // UserData, which has to go back to get here.
  const handleChooseNewSlot = () => {
    setSelectedSlot(null)
    updateMutation.reset()
    queryClient.invalidateQueries({ queryKey: ['employee-for-timeslots'] })
  }

  // ── Booking fetch (reschedule deep-link: ?screen=timeslot&booking-id=…) ─
  const bookingQuery = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: getBooking,
    enabled: Boolean(bookingId),
  })

  // Hydrate store from booking response (same fields as the old booking-tool)
  useEffect(() => {
    const b = bookingQuery.data
    if (!b) return
    setState({
      wwLocationId: b.locationId,
      serviceId: b.serviceIds?.[0],
      wwResourceId: b.resourceIds?.[0],
      duration: b.duration,
      timeslot: b.date,
    })
    // Mark the current booking slot; the day is selected once `days` are ready
    // so it lines up with a carousel date from fillDays.
    setRescheduleSlot(b.date)
    setSelectedSlot(b.date)
  }, [bookingQuery.data, setState])

  // Resolve store (locationCode / storeName) from the booking's Waitwhile location
  const storeQuery = useQuery({
    queryKey: ['store-by-location', bookingQuery.data?.locationId],
    queryFn: () => getStoreDataByLocation(bookingQuery.data!.locationId),
    enabled: Boolean(bookingId && bookingQuery.data?.locationId),
  })

  useEffect(() => {
    const s = storeQuery.data
    if (!s) return
    setState({
      locationCode: s.locationCode,
      storeName: s.name,
      storeId: s.storeIdC,
      wwLocationId: s.waitwhileLocationIdC,
      location: s.countryCodeC.toLowerCase(),
    })
  }, [storeQuery.data, setState])

  // ── Employee data ───────────────────────────────────────────────────────
  // From EmployeesList: use selectedEmployee (no extra call).
  // From booking-id deep-link: fetch by Waitwhile resource id after store is ready.
  // From employee-id deep-link: fetch by CRM employee id.
  const needsEmployeeFetch = !selectedEmployee
  // Reschedule: wait until booking + store hydrate have written locationCode.
  // resourceIds may be empty for "no preference" bookings.
  const rescheduleReady = Boolean(
    bookingId && locationCode && bookingQuery.isSuccess && storeQuery.isSuccess,
  )
  const directEmployeeReady = Boolean(!bookingId && locationCode)
  const canFetchEmployee = bookingId ? rescheduleReady : directEmployeeReady

  const employeeQuery = useQuery({
    queryKey: [
      'employee-for-timeslots',
      employeeId ?? null,
      wwResourceId ?? null,
      locationCode,
      bookingId,
    ],
    queryFn: () => {
      if (wwResourceId) return getEmployeeDataByWwResourceId(wwResourceId)
      if (employeeId) return getEmployeeDataById(employeeId)
      return getNoPreferenceEmployee()
    },
    enabled: needsEmployeeFetch && canFetchEmployee,
  })

  // The resolved employee: prefer the one carried from EmployeesList, fall
  // back to the API response for direct-landing flows.
  const employee = selectedEmployee ?? employeeQuery.data ?? null

  // ── Build display days ──────────────────────────────────────────────────
  const days = useMemo(() => {
    const availability = employee?.availability ?? []
    if (availability.length === 0) return []
    const filled = fillDays(availability)
    if (!rescheduleSlot) return filled

    // Ensure the currently booked slot is always visible on its day,
    // even if the availability API omits it.
    const datePart = toDatePart(rescheduleSlot)
    return filled.map((day) => {
      if (toDatePart(day.date) !== datePart) return day
      const exists = day.timeSlots.some((s) =>
        isSameMinute(s.start, rescheduleSlot),
      )
      if (exists) return day
      return {
        ...day,
        timeSlots: [
          ...day.timeSlots,
          { start: rescheduleSlot, available: false },
        ].sort((a, b) => a.start.localeCompare(b.start)),
      }
    })
  }, [employee, rescheduleSlot])

  const hasAvailability = days.some(isDayAvailable)

  // Once days are ready, pick an initial day if none is selected yet.
  // For reschedule, prefer the day of the existing booking.
  useEffect(() => {
    if (days.length === 0 || selectedDate) return
    if (rescheduleSlot) {
      const match = days.find(
        (d) => toDatePart(d.date) === toDatePart(rescheduleSlot),
      )
      if (match) {
        setSelectedDate(match.date)
        return
      }
    }
    const first = days.find(isDayAvailable)
    if (first) setSelectedDate(first.date)
  }, [days, selectedDate, rescheduleSlot])

  const currentDay =
    days.find(
      (d) => selectedDate && toDatePart(d.date) === toDatePart(selectedDate),
    ) ?? null

  const handleDaySelect = (date: string) => {
    if (selectedDate && toDatePart(date) === toDatePart(selectedDate)) return
    setSelectedDate(date)
    setSelectedSlot(null)
    const day = days.find((d) => toDatePart(d.date) === toDatePart(date))
    Avo.bookingToolDateSelected({
      ...flowProperties(),
      selectedDate: toDatePart(date),
      availableSlots: day?.timeSlots.filter((s) => s.available).length ?? 0,
    })
  }

  const handleSlotSelect = (start: string) => {
    if (selectedSlot && isSameMinute(start, selectedSlot)) {
      setSelectedSlot(null)
      return
    }
    setSelectedSlot(start)
    Avo.bookingToolTimeslotSelected(flowProperties())
  }

  const handleBackToAdvisors = () => {
    Avo.bookingToolAllStyleAdvisors(flowProperties())
    // Clear the carried employee object — EmployeesList will fetch fresh data
    // on its next mount via refetchOnMount: 'always'.
    setState({ selectedEmployee: undefined })
    navigate('employees-list')
  }

  // ── Render helpers ──────────────────────────────────────────────────────
  const isLoading =
    (needsEmployeeFetch && employeeQuery.isLoading) ||
    (isReschedule && (bookingQuery.isLoading || storeQuery.isLoading))
  const isError =
    (needsEmployeeFetch && employeeQuery.isError) ||
    (isReschedule && (bookingQuery.isError || storeQuery.isError))

  // Same load analytics as the old Stencil employee-availability screen.
  // Guarded so this only counts once per mount, not on every render while
  // loading/employee settle.
  const viewedRef = useRef(false)
  useEffect(() => {
    if (isLoading || isError || viewedRef.current) return
    // Wait until we have something to show (employee carried or fetched).
    if (!employee && needsEmployeeFetch) return
    viewedRef.current = true
    Avo.bookingToolStepViewed(flowProperties())
  }, [isLoading, isError, employee, needsEmployeeFetch])

  const erroredRef = useRef(false)
  useEffect(() => {
    if (!isError) {
      erroredRef.current = false
      return
    }
    if (erroredRef.current) return
    erroredRef.current = true
    Avo.bookingToolError({
      ...flowProperties(),
      errorType: ErrorType.TIMESLOTS_FAILED,
    })
  }, [isError])

  const employeeImage = employee?.isNoPreferenceEmployee
    ? NO_PREFERENCE_IMAGE
    : employee
      ? resolveEmployeeImage(employee)
      : null

  const employeeName = employee?.isNoPreferenceEmployee
    ? null
    : (employee?.name ?? store.employeeName ?? null)

  const storeName = store.storeName

  const handleContinue = () => {
    if (!selectedSlot) return

    setState({
      timeslot: selectedSlot,
      employeeImage: employeeImage ?? NO_PREFERENCE_IMAGE,
      employeeName: employeeName ?? undefined,
      // An `employee-id` deep link resolves the advisor here, not in
      // EmployeesList, so this is the only place the Waitwhile resource id
      // reaches the store for createBooking's `resourceIds`.
      ...(employee && {
        wwResourceId: employee.isNoPreferenceEmployee ? undefined : employee.id,
      }),
    })

    Avo.bookingToolContinueWithAppointmentSelected(flowProperties())
    Avo.bookingToolStepCompleted(flowProperties())

    if (isReschedule) {
      updateMutation.mutate()
      return
    }

    navigate('user-data')
  }

  // "Continue" is available when a slot is selected and it's not the unchanged reschedule slot
  const canContinue =
    Boolean(selectedSlot) &&
    !(
      isReschedule &&
      rescheduleSlot &&
      isSameMinute(selectedSlot!, rescheduleSlot)
    )

  // Employee resolved but has no bookable slots (empty schedule or fully booked).
  // In this state the title, avatar, and date carousel are hidden so the
  // message fills the screen centered.
  const noAvailability = employee !== null && !hasAvailability

  if (isSlotUnavailable) {
    return (
      <SlotUnavailable
        onClose={onClose}
        onChooseNewSlot={handleChooseNewSlot}
      />
    )
  }

  return (
    <ScreenLayout
      title={noAvailability ? undefined : t('timeslots.title')}
      titleTestId="timeslots-heading"
      onClose={onClose}
    >
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader ariaLabel={t('common.loading')} className="h-auto w-auto" />
        </div>
      )}

      {isError && !isLoading && (
        <div className="flex-1 flex flex-col items-center justify-center gap-24 px-32 text-center">
          <p
            className="text-base leading-[24px] text-text"
            data-testid="timeslots-error"
          >
            {t('common.error')}
          </p>
          <Button
            variant="outlined"
            size="m"
            onClick={() => {
              if (needsEmployeeFetch) employeeQuery.refetch()
              if (isReschedule) {
                bookingQuery.refetch()
                storeQuery.refetch()
              }
            }}
            data-testid="timeslots-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      {!isLoading && !isError && noAvailability && (
        <div className="flex-1 flex flex-col items-center justify-center gap-16 px-32 text-center">
          <p
            className="text-[20px] leading-[30px] font-medium text-text tracking-[-0.3px]"
            data-testid="timeslots-no-availability-title"
          >
            {t('timeslots.noAvailabilityTitle')}
          </p>
          <p className="text-base leading-[24px] font-light text-text">
            {t('timeslots.noAvailabilityBody')}
          </p>
          <Button
            variant="outlined"
            size="m"
            onClick={handleBackToAdvisors}
            data-testid="timeslots-see-all-advisors"
          >
            {t('timeslots.seeAllAdvisors')}
          </Button>
        </div>
      )}

      {!isLoading && !isError && !noAvailability && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/*
            Everything above the footer scrolls as one region.

            The avatar, name, store line and date tabs are all fixed height, and
            together with the footer they outgrow the panel on a shorter viewport.
            While each sat in its own `shrink-0` row the parent's
            `overflow-hidden` simply cut the overflow off — the date tabs lost
            their lower edge and Continue ended up below the fold, reachable only
            by scrolling whichever ancestor happened to scroll.

            Trade-off: the carousel is no longer pinned when the content does not
            fit. On a viewport with room nothing scrolls and this renders exactly
            as before; on one without, scrolling to the tabs beats clipping them.
          */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Employee info */}
            <div className="flex flex-col items-center gap-12 pb-32 px-16">
              {employeeImage && (
                <img
                  src={employeeImage}
                  alt={employeeName ?? t('employeesList.noPreference')}
                  className="w-[76px] h-[76px] rounded-full object-cover"
                  data-testid="timeslots-employee-avatar"
                />
              )}
              <div className="flex flex-col items-center gap-[2px] text-text text-center">
                {employeeName && (
                  <p
                    className="text-[18px] leading-[28px] font-medium tracking-[-0.18px]"
                    data-testid="timeslots-employee-name"
                  >
                    {employeeName}
                  </p>
                )}
                {storeName && (
                  <p
                    className="text-[14px] leading-[20px] font-light"
                    data-testid="timeslots-store-name"
                  >
                    {storeName} {t('timeslots.store')}
                  </p>
                )}
              </div>
            </div>

            {/* Date carousel */}
            {selectedDate && (
              <DayPicker
                days={days}
                selectedDate={selectedDate}
                onSelect={handleDaySelect}
              />
            )}

            {/* Timeslots grid */}
            <div className="mt-24 pb-24">
              {currentDay && currentDay.timeSlots.length > 0 && (
                <TimeSlotsGrid
                  day={currentDay}
                  selectedSlot={selectedSlot}
                  rescheduleSlot={rescheduleSlot}
                  onSelect={handleSlotSelect}
                />
              )}
            </div>
          </div>

          {/*
            Footer — pinned, and inset 20px like the UserData and Confirmation
            footers (`-mx-16 px-20`) so Continue is the same width as Confirm
            appointment and Done. Inside ScreenLayout's own `px-16` it was 32px
            in, which read as a narrower button.
          */}
          <div className="shrink-0 -mx-16 flex flex-col px-20">
            {!advisorStepSkipped && (
              <div className="py-16 text-center">
                <p className="text-[14px] leading-[20px] font-light text-text inline">
                  {t('timeslots.preferDifferent')}{' '}
                </p>
                <button
                  type="button"
                  onClick={handleBackToAdvisors}
                  className="text-[14px] leading-[20px] font-light text-text underline"
                  data-testid="timeslots-see-all-link"
                >
                  {t('timeslots.seeAllAdvisors')}
                </button>
              </div>
            )}
            {canContinue && (
              <div className="flex flex-col gap-12 pb-[calc(20px+env(safe-area-inset-bottom,0px))]">
                {updateMutation.isError && (
                  <p
                    className="text-[14px] leading-[20px] font-light text-text text-center"
                    data-testid="timeslots-update-error"
                  >
                    {t('common.error')}
                  </p>
                )}
                <Button
                  className="w-full"
                  size="l"
                  onClick={handleContinue}
                  loading={updateMutation.isPending}
                  data-testid="timeslots-continue"
                >
                  {isReschedule
                    ? t('timeslots.rescheduleButton')
                    : t('timeslots.continueButton')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </ScreenLayout>
  )
}
